import React, { useState } from 'react';
import RecruitmentXLogo from './rx_logo_white.svg';
import { getCountries } from '../utils/countries';
import LegalModal from './LegalModal';

const SignupPage = () => {
    const [currentStep, setCurrentStep] = useState(1); // 1: Organization + Primary User, 2: Manage Users
    const [organizationData, setOrganizationData] = useState({
        companyName: '',
        country: '',
        abn: '',
        companyAddress: '',
        city: '',
        state: '',
        postalCode: '',
        primaryUser: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            password: '',
            confirmPassword: ''
        }
    });
    const [users, setUsers] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [showLegalModal, setShowLegalModal] = useState(false);
    const [legalModalType, setLegalModalType] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);

    const backend_url = process.env.REACT_APP_BACKEND_URL || '';
    const signup_url = backend_url + '/v1/create-corporation-account';

    const handleOrganizationChange = (e) => {
        const { name, value } = e.target;
        setOrganizationData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handlePrimaryUserChange = (e) => {
        const { name, value } = e.target;
        setOrganizationData(prev => ({
            ...prev,
            primaryUser: {
                ...prev.primaryUser,
                [name]: value
            }
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleUserChange = (index, e) => {
        const { name, value } = e.target;
        setUsers(prev => prev.map((user, i) => 
            i === index ? { ...user, [name]: value } : user
        ));
        if (errors[`user_${index}_${name}`]) {
            setErrors(prev => ({
                ...prev,
                [`user_${index}_${name}`]: ''
            }));
        }
    };

    const addUser = () => {
        setUsers(prev => [...prev, {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            password: '',
            confirmPassword: ''
        }]);
    };

    const removeUser = (index) => {
        setUsers(prev => prev.filter((_, i) => i !== index));
    };

    const validateStep1 = () => {
        const newErrors = {};

        if (!organizationData.companyName.trim()) {
            newErrors.companyName = 'Company name is required';
        }

        if (!organizationData.country.trim()) {
            newErrors.country = 'Country is required';
        }

        // Address validation - if any address field is filled, validate others
        const addressFields = [
            organizationData.companyAddress,
            organizationData.city,
            organizationData.state,
            organizationData.postalCode
        ];
        const hasAnyAddress = addressFields.some(field => field && field.trim());
        
        if (hasAnyAddress) {
            if (!organizationData.companyAddress.trim()) {
                newErrors.companyAddress = 'Company address is required';
            }
            if (!organizationData.city.trim()) {
                newErrors.city = 'City is required';
            }
            if (!organizationData.state.trim()) {
                newErrors.state = 'State/Province is required';
            }
            if (!organizationData.postalCode.trim()) {
                newErrors.postalCode = 'Postal code is required';
            }
        }

        const { primaryUser } = organizationData;
        if (!primaryUser.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!primaryUser.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!primaryUser.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(primaryUser.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!primaryUser.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[\d\s\-\+\(\)]+$/.test(primaryUser.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }
        if (!primaryUser.password) {
            newErrors.password = 'Password is required';
        } else if (primaryUser.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }
        if (primaryUser.password !== primaryUser.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (!termsAccepted || !privacyAccepted) {
            newErrors.termsAccepted = 'You must accept the Terms of Service and Privacy Policy';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors = {};

        users.forEach((user, index) => {
            if (!user.firstName.trim()) {
                newErrors[`user_${index}_firstName`] = 'First name is required';
            }
            if (!user.lastName.trim()) {
                newErrors[`user_${index}_lastName`] = 'Last name is required';
            }
            if (!user.email.trim()) {
                newErrors[`user_${index}_email`] = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(user.email)) {
                newErrors[`user_${index}_email`] = 'Please enter a valid email';
            }
            if (user.phone.trim() && !/^[\d\s\-\+\(\)]+$/.test(user.phone)) {
                newErrors[`user_${index}_phone`] = 'Please enter a valid phone number';
            }
            if (!user.password) {
                newErrors[`user_${index}_password`] = 'Password is required';
            } else if (user.password.length < 8) {
                newErrors[`user_${index}_password`] = 'Password must be at least 8 characters';
            }
            if (user.password !== user.confirmPassword) {
                newErrors[`user_${index}_confirmPassword`] = 'Passwords do not match';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleStep1Submit = (e) => {
        e.preventDefault();
        if (validateStep1()) {
            setCurrentStep(2);
        }
    };

    const handleStep2Submit = (e) => {
        e.preventDefault();
        if (validateStep2()) {
            handleFinalSubmit();
        }
    };

    const handleFinalSubmit = async () => {
        setIsLoading(true);
        setMessage('');

        try {
            const allUsers = [
                {
                    firstName: organizationData.primaryUser.firstName,
                    lastName: organizationData.primaryUser.lastName,
                    username: organizationData.primaryUser.email.toLowerCase(),
                    password: organizationData.primaryUser.password,
                    phone: organizationData.primaryUser.phone,
                    termsAccepted: termsAccepted,
                    privacyAccepted: privacyAccepted
                },
                ...users.map(user => ({
                    firstName: user.firstName,
                    lastName: user.lastName,
                    username: user.email.toLowerCase(),
                    password: user.password,
                    phone: user.phone, 
                    termsAccepted: termsAccepted,
                    privacyAccepted: privacyAccepted
                }))
            ];

            const response = await fetch(signup_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    companyName: organizationData.companyName,
                    country: organizationData.country,
                    abn: organizationData.abn,
                    companyAddress: organizationData.companyAddress,
                    city: organizationData.city,
                    state: organizationData.state,
                    postalCode: organizationData.postalCode,
                    users: allUsers,
                    primaryUserIndex: 0,
                    termsAccepted: termsAccepted,
                    privacyAccepted: privacyAccepted
                })
            });

            const data = await response.json();
            console.log('Response:', data);

            if (data.success || response.ok) {
                const userInfo = {
                    corporateId: data.corporate_id,
                    companyName: organizationData.companyName,
                    primaryUser: organizationData.primaryUser,
                    users: users
                };
                localStorage.setItem('pendingCorporation', JSON.stringify(userInfo));
                
                setSuccess(true);
                setMessage('Corporation account created successfully! Redirecting to plan selection...');
                setTimeout(() => {
                    window.location.href = '/subscription-plans';
                }, 2000);
            } else {
                setMessage(data.error || 'Failed to create account. Please try again.');
            }
        } catch (error) {
            console.error('Signup error:', error);
            setMessage('Failed to create account. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLegalModalOpen = (type) => {
        setLegalModalType(type);
        setShowLegalModal(true);
    };

    const handleLegalModalClose = () => {
        setShowLegalModal(false);
        setLegalModalType('');
    };

    const handleCheckboxChange = (type) => {
        if (type === 'terms') {
            setTermsAccepted(!termsAccepted);
            if (errors.termsAccepted) {
                setErrors(prev => ({
                    ...prev,
                    termsAccepted: ''
                }));
            }
        } else if (type === 'privacy') {
            setPrivacyAccepted(!privacyAccepted);
            if (errors.privacyAccepted) {
                setErrors(prev => ({
                    ...prev,
                    privacyAccepted: ''
                }));
            }
        }
    };

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#141414',
            padding: '20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        card: {
            background: '#1a1a1a',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            padding: '40px',
            width: '100%',
            maxWidth: '750px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
        },
        header: {
            textAlign: 'center',
            marginBottom: '30px'
        },
        logo: {
            height: '60px',
            width: 'auto',
            marginBottom: '20px',
            display: 'block',
            margin: '0 auto 20px'
        },
        title: {
            fontSize: '24px',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '8px',
            marginTop: '20px'
        },
        subtitle: {
            fontSize: '14px',
            color: '#a0a0a0'
        },
        formRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '15px',
            marginBottom: '20px'
        },
        formGroup: {
            marginBottom: '20px'
        },
        label: {
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#e0e0e0'
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            background: '#2a2a2a',
            border: '1px solid #3a3a3a',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#ffffff',
            transition: 'all 0.3s',
            boxSizing: 'border-box',
            outline: 'none'
        },
        inputError: {
            borderColor: '#ff4444'
        },
        inputFocus: {
            borderColor: '#FF0066',
            background: '#333333'
        },
        errorMessage: {
            color: '#ff4444',
            fontSize: '12px',
            marginTop: '5px',
            display: 'block'
        },
        button: {
            width: '100%',
            padding: '14px',
            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            marginTop: '20px'
        },
        buttonHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 20px rgba(255, 0, 102, 0.3)'
        },
        buttonDisabled: {
            opacity: '0.6',
            cursor: 'not-allowed'
        },
        message: {
            padding: '12px',
            borderRadius: '8px',
            marginTop: '15px',
            fontSize: '14px',
            textAlign: 'center'
        },
        messageSuccess: {
            background: 'rgba(46, 160, 67, 0.15)',
            color: '#2ea043',
            border: '1px solid rgba(46, 160, 67, 0.3)'
        },
        messageError: {
            background: 'rgba(248, 81, 73, 0.15)',
            color: '#f85149',
            border: '1px solid rgba(248, 81, 73, 0.3)'
        },
        footer: {
            textAlign: 'center',
            marginTop: '24px',
            fontSize: '14px',
            color: '#a0a0a0'
        },
        link: {
            color: '#FF0066',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'color 0.3s'
        },
        linkHover: {
            color: '#FF66CC',
            textDecoration: 'underline'
        },
        disclaimer: {
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#2a2a2a',
            borderRadius: '8px',
            border: '1px solid #3a3a3a'
        },
        disclaimerText: {
            fontSize: '13px',
            color: '#e0e0e0',
            lineHeight: '1.5',
            marginBottom: '15px'
        },
        checkboxContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: '10px',
            cursor: 'pointer'
        },
        checkbox: {
            marginRight: '10px',
            marginTop: '2px',
            accentColor: '#FF0066'
        },
        checkboxLabel: {
            fontSize: '13px',
            color: '#e0e0e0',
            lineHeight: '1.4',
            cursor: 'pointer',
            userSelect: 'none'
        },
        legalLink: {
            color: '#FF0066',
            textDecoration: 'none',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'color 0.3s'
        },
        errorText: {
            color: '#ff4444',
            fontSize: '12px',
            marginTop: '5px'
        },
        stepHeader: {
            textAlign: 'center',
            marginBottom: '30px'
        },
        stepTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '8px'
        },
        stepSubtitle: {
            fontSize: '14px',
            color: '#a0a0a0'
        },
        userRow: {
            background: '#2a2a2a',
            borderRadius: '8px',
            padding: '15px 35px',
            marginBottom: '12px',
            border: '1px solid #3a3a3a',
            position: 'relative'
        },
        userRowHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
        },
        userRowTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#ffffff'
        },
        removeUserButton: {
            background: '#ff4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        userRowFields: {
            display: 'grid',
            gridTemplateColumns: '5fr 5fr 15fr 12fr 8fr 8fr',
            gap: '15px',
            alignItems: 'center'
        },
        userField: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch'
        },
        userFieldLabel: {
            fontSize: '13px',
            fontWeight: '500',
            color: '#a0a0a0',
            marginBottom: '6px'
        },
        userInput: {
            width: '100%',
            padding: '12px 14px',
            background: '#1a1a1a',
            border: '1px solid #3a3a3a',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#ffffff',
            transition: 'all 0.3s',
            boxSizing: 'border-box',
            outline: 'none'
        },
        userErrorMessage: {
            color: '#ff4444',
            fontSize: '12px',
            marginTop: '3px',
            display: 'block',
            height: '16px',
            lineHeight: '16px'
        },
        addUserButton: {
            width: '100%',
            padding: '12px',
            background: '#FF0066',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.3s',
            marginBottom: '20px'
        },
        stepButtons: {
            display: 'flex',
            gap: '15px',
            marginTop: '20px'
        },
        backButton: {
            flex: 1,
            padding: '14px',
            background: 'transparent',
            color: '#a0a0a0',
            border: '1px solid #3a3a3a',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s'
        }
    };

    const renderStep1 = () => (
        <div>
            <div style={styles.formGroup}>
                <label style={styles.label}>Company Name</label>
                <input
                    type="text"
                    name="companyName"
                    value={organizationData.companyName}
                    onChange={handleOrganizationChange}
                    style={{
                        ...styles.input,
                        ...(errors.companyName ? styles.inputError : {})
                    }}
                    placeholder="Your Company"
                />
                {errors.companyName && (
                    <span style={styles.errorMessage}>{errors.companyName}</span>
                )}
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>Country</label>
                <select
                    name="country"
                    value={organizationData.country}
                    onChange={handleOrganizationChange}
                    style={{
                        ...styles.input,
                        ...(errors.country ? styles.inputError : {})
                    }}
                >
                    <option value="">Select your country</option>
                    {getCountries().map(country => (
                        <option key={country} value={country}>
                            {country}
                        </option>
                    ))}
                </select>
                {errors.country && (
                    <span style={styles.errorMessage}>{errors.country}</span>
                )}
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>
                    ABN / Business Registration Number 
                    <span style={{ color: '#a0a0a0', fontWeight: '400', marginLeft: '5px' }}>(Optional)</span>
                </label>
                <input
                    type="text"
                    name="abn"
                    value={organizationData.abn}
                    onChange={handleOrganizationChange}
                    style={{
                        ...styles.input,
                        ...(errors.abn ? styles.inputError : {})
                    }}
                    placeholder="12 345 678 901"
                />
                {errors.abn && (
                    <span style={styles.errorMessage}>{errors.abn}</span>
                )}
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>Company Address</label>
                <input
                    type="text"
                    name="companyAddress"
                    value={organizationData.companyAddress}
                    onChange={handleOrganizationChange}
                    style={{
                        ...styles.input,
                        ...(errors.companyAddress ? styles.inputError : {})
                    }}
                    placeholder="123 Business St"
                />
                {errors.companyAddress && (
                    <span style={styles.errorMessage}>{errors.companyAddress}</span>
                )}
            </div>

            <div style={styles.formRow}>
                <div>
                    <label style={styles.label}>City</label>
                    <input
                        type="text"
                        name="city"
                        value={organizationData.city}
                        onChange={handleOrganizationChange}
                        style={{
                            ...styles.input,
                            ...(errors.city ? styles.inputError : {})
                        }}
                        placeholder="Sydney"
                    />
                    {errors.city && (
                        <span style={styles.errorMessage}>{errors.city}</span>
                    )}
                </div>

                <div>
                    <label style={styles.label}>State / Province</label>
                    <input
                        type="text"
                        name="state"
                        value={organizationData.state}
                        onChange={handleOrganizationChange}
                        style={{
                            ...styles.input,
                            ...(errors.state ? styles.inputError : {})
                        }}
                        placeholder="NSW"
                    />
                    {errors.state && (
                        <span style={styles.errorMessage}>{errors.state}</span>
                    )}
                </div>
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>Postal / ZIP Code</label>
                <input
                    type="text"
                    name="postalCode"
                    value={organizationData.postalCode}
                    onChange={handleOrganizationChange}
                    style={{
                        ...styles.input,
                        ...(errors.postalCode ? styles.inputError : {})
                    }}
                    placeholder="2000"
                />
                {errors.postalCode && (
                    <span style={styles.errorMessage}>{errors.postalCode}</span>
                )}
            </div>

            <div style={styles.formRow}>
                <div>
                    <label style={styles.label}>First Name</label>
                    <input
                        type="text"
                        name="firstName"
                        value={organizationData.primaryUser.firstName}
                        onChange={handlePrimaryUserChange}
                        style={{
                            ...styles.input,
                            ...(errors.firstName ? styles.inputError : {})
                        }}
                        placeholder="John"
                    />
                    {errors.firstName && (
                        <span style={styles.errorMessage}>{errors.firstName}</span>
                    )}
                </div>

                <div>
                    <label style={styles.label}>Last Name</label>
                    <input
                        type="text"
                        name="lastName"
                        value={organizationData.primaryUser.lastName}
                        onChange={handlePrimaryUserChange}
                        style={{
                            ...styles.input,
                            ...(errors.lastName ? styles.inputError : {})
                        }}
                        placeholder="Doe"
                    />
                    {errors.lastName && (
                        <span style={styles.errorMessage}>{errors.lastName}</span>
                    )}
                </div>
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>Work Email</label>
                <input
                    type="email"
                    name="email"
                    value={organizationData.primaryUser.email}
                    onChange={handlePrimaryUserChange}
                    style={{
                        ...styles.input,
                        ...(errors.email ? styles.inputError : {})
                    }}
                    placeholder="john@company.com"
                />
                {errors.email && (
                    <span style={styles.errorMessage}>{errors.email}</span>
                )}
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number</label>
                <input
                    type="tel"
                    name="phone"
                    value={organizationData.primaryUser.phone}
                    onChange={handlePrimaryUserChange}
                    required
                    style={{
                        ...styles.input,
                        ...(errors.phone ? styles.inputError : {})
                    }}
                    placeholder="+1 234 567 8900"
                />
                {errors.phone && (
                    <span style={styles.errorMessage}>{errors.phone}</span>
                )}
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <input
                    type="password"
                    name="password"
                    value={organizationData.primaryUser.password}
                    onChange={handlePrimaryUserChange}
                    style={{
                        ...styles.input,
                        ...(errors.password ? styles.inputError : {})
                    }}
                    placeholder="At least 8 characters"
                />
                {errors.password && (
                    <span style={styles.errorMessage}>{errors.password}</span>
                )}
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>Confirm Password</label>
                <input
                    type="password"
                    name="confirmPassword"
                    value={organizationData.primaryUser.confirmPassword}
                    onChange={handlePrimaryUserChange}
                    style={{
                        ...styles.input,
                        ...(errors.confirmPassword ? styles.inputError : {})
                    }}
                    placeholder="Re-enter your password"
                />
                {errors.confirmPassword && (
                    <span style={styles.errorMessage}>{errors.confirmPassword}</span>
                )}
            </div>

            {/* Legal Agreement Checkbox */}
            <div style={styles.checkboxContainer} onClick={() => {
                handleCheckboxChange('terms');
                handleCheckboxChange('privacy');
            }}>
                <input
                    type="checkbox"
                    checked={termsAccepted && privacyAccepted}
                    onChange={() => {
                        handleCheckboxChange('terms');
                        handleCheckboxChange('privacy');
                    }}
                    style={styles.checkbox}
                />
                <label style={styles.checkboxLabel}>
                    I have read and agree to the{' '}
                    <span 
                        style={styles.legalLink}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleLegalModalOpen('terms');
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#FF66CC'}
                        onMouseLeave={(e) => e.target.style.color = '#FF0066'}
                    >
                        Terms of Service
                    </span>
                    {' '}and{' '}
                    <span 
                        style={styles.legalLink}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleLegalModalOpen('privacy');
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#FF66CC'}
                        onMouseLeave={(e) => e.target.style.color = '#FF0066'}
                    >
                        Privacy Policy
                    </span>
                </label>
            </div>
            {(errors.termsAccepted || errors.privacyAccepted) && (
                <div style={styles.errorText}>
                    {errors.termsAccepted || errors.privacyAccepted}
                </div>
            )}

            <button
                onClick={handleStep1Submit}
                style={{
                    ...styles.button,
                    ...(isLoading ? styles.buttonDisabled : {})
                }}
                disabled={isLoading}
                onMouseEnter={(e) => {
                    if (!isLoading) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 10px 20px rgba(255, 0, 102, 0.3)';
                    }
                }}
                onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                }}
            >
                Manage Users
            </button>
        </div>
    );

    const renderStep2 = () => (
        <div>
            <div style={styles.stepHeader}>
                <h2 style={styles.stepTitle}>Add Team Members</h2>
                <p style={styles.stepSubtitle}>
                    Add additional users to your organization (optional)
                </p>
            </div>

            {users.map((user, index) => (
                <div key={index} style={styles.userRow}>
                    <div style={styles.userRowHeader}>
                        <span style={styles.userRowTitle}>User {index + 1}</span>
                        <button
                            type="button"
                            onClick={() => removeUser(index)}
                            style={styles.removeUserButton}
                        >
                            ×
                        </button>
                    </div>

                    <div style={styles.userRowFields}>
                        <div style={styles.userField}>
                            <label style={styles.userFieldLabel}>First</label>
                            <input
                                type="text"
                                name="firstName"
                                value={user.firstName}
                                onChange={(e) => handleUserChange(index, e)}
                                style={{
                                    ...styles.userInput,
                                    ...(errors[`user_${index}_firstName`] ? styles.inputError : {})
                                }}
                                placeholder="Jane"
                            />
                            <span style={styles.userErrorMessage}>
                                {errors[`user_${index}_firstName`] || ''}
                            </span>
                        </div>

                        <div style={styles.userField}>
                            <label style={styles.userFieldLabel}>Last</label>
                            <input
                                type="text"
                                name="lastName"
                                value={user.lastName}
                                onChange={(e) => handleUserChange(index, e)}
                                style={{
                                    ...styles.userInput,
                                    ...(errors[`user_${index}_lastName`] ? styles.inputError : {})
                                }}
                                placeholder="Smith"
                            />
                            <span style={styles.userErrorMessage}>
                                {errors[`user_${index}_lastName`] || ''}
                            </span>
                        </div>

                        <div style={styles.userField}>
                            <label style={styles.userFieldLabel}>Email</label>
                            <input
                                type="email"
                                name="email"
                                value={user.email}
                                onChange={(e) => handleUserChange(index, e)}
                                style={{
                                    ...styles.userInput,
                                    ...(errors[`user_${index}_email`] ? styles.inputError : {})
                                }}
                                placeholder="jane@company.com"
                            />
                            <span style={styles.userErrorMessage}>
                                {errors[`user_${index}_email`] || ''}
                            </span>
                        </div>

                        <div style={styles.userField}>
                            <label style={styles.userFieldLabel}>Phone</label>
                            <input
                                type="tel"
                                name="phone"
                                value={user.phone}
                                onChange={(e) => handleUserChange(index, e)}
                                style={{
                                    ...styles.userInput,
                                    ...(errors[`user_${index}_phone`] ? styles.inputError : {})
                                }}
                                placeholder="+1 234 567 8900"
                            />
                            <span style={styles.userErrorMessage}>
                                {errors[`user_${index}_phone`] || ''}
                            </span>
                        </div>

                        <div style={styles.userField}>
                            <label style={styles.userFieldLabel}>Password</label>
                            <input
                                type="password"
                                name="password"
                                value={user.password}
                                onChange={(e) => handleUserChange(index, e)}
                                style={{
                                    ...styles.userInput,
                                    ...(errors[`user_${index}_password`] ? styles.inputError : {})
                                }}
                                placeholder="8+ chars"
                            />
                            <span style={styles.userErrorMessage}>
                                {errors[`user_${index}_password`] || ''}
                            </span>
                        </div>

                        <div style={styles.userField}>
                            <label style={styles.userFieldLabel}>Confirm</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                value={user.confirmPassword}
                                onChange={(e) => handleUserChange(index, e)}
                                style={{
                                    ...styles.userInput,
                                    ...(errors[`user_${index}_confirmPassword`] ? styles.inputError : {})
                                }}
                                placeholder="Re-enter"
                            />
                            <span style={styles.userErrorMessage}>
                                {errors[`user_${index}_confirmPassword`] || ''}
                            </span>
                        </div>
                    </div>
                </div>
            ))}

            <button
                type="button"
                onClick={addUser}
                style={styles.addUserButton}
                onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#FF66CC';
                }}
                onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#FF0066';
                }}
            >
                + Add Another User
            </button>

            {message && (
                <div style={{
                    ...styles.message,
                    ...(success ? styles.messageSuccess : styles.messageError)
                }}>
                    {message}
                </div>
            )}

            <div style={styles.stepButtons}>
                <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    style={styles.backButton}
                >
                    Back
                </button>
                <button
                    onClick={handleStep2Submit}
                    style={{
                        ...styles.button,
                        ...(isLoading ? styles.buttonDisabled : {})
                    }}
                    disabled={isLoading}
                    onMouseEnter={(e) => {
                        if (!isLoading) {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 10px 20px rgba(255, 0, 102, 0.3)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                    }}
                >
                    {isLoading ? 'Creating Account...' : 'Choose your plan'}
                </button>
            </div>
        </div>
    );

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.header}>
                    <img src={RecruitmentXLogo} alt="RecruitmentX" style={styles.logo} />
                </div>

                {currentStep === 1 ? renderStep1() : renderStep2()}

                <div style={styles.footer}>
                    <p>
                        Already have an account?{' '}
                        <a
                            href="/login"
                            style={styles.link}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#FF66CC';
                                e.target.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#FF0066';
                                e.target.style.textDecoration = 'none';
                            }}
                        >
                            Sign in
                        </a>
                    </p>
                </div>
            </div>
            
            {/* Legal Modal */}
            <LegalModal 
                isOpen={showLegalModal}
                onClose={handleLegalModalClose}
                type={legalModalType}
            />
        </div>
    );
};

export default SignupPage;